import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { IntelligenceDeclinedWorkModel }     from '../typings/intelligence.opportunities';
import { IntelligenceFormatService }         from '../intelligence.format.service';
import { IntelligenceLapsedCustomerModel }   from '../typings/intelligence.opportunities';
import { IntelligenceListPageModel }         from '../typings/intelligence.list-page';
import { IntelligenceOpportunitiesModel }    from '../typings/intelligence.opportunities';
import { IntelligenceOverdueServiceModel }   from '../typings/intelligence.opportunities';
import { IntelligenceService }               from '../intelligence.service';
import { IntelligenceUnconvertedQuoteModel } from '../typings/intelligence.opportunities';
import { MessengerService }                  from '../../messages/messenger/messenger.service';
import { TableSortModel }                    from '../../shared/components/charts/typings/table-sort';

// server-side page size; the true totals ride with the payload for the pagers
const LIST_PAGE_SIZE = 25;

const LIST_DEFAULT_SORTS : { [ list : string ] : TableSortModel } = {
  declined_work     : { direction: 'desc', field: 'value_ex_tax' },
  lapsed_customers  : { direction: 'desc', field: 'lifetime_value' },
  overdue_services  : { direction: 'asc', field: 'days_overdue' },
  unconverted_quotes: { direction: 'desc', field: 'value_ex_tax' }
};

@Component({
  selector   : 'wor-intelligence-opportunities-tab',
  template: require('./intelligence.opportunities-tab.component.html')
})
export class OpportunitiesTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  counts : { [ list : string ] : number } = { declined_work: 0, lapsed_customers: 0, overdue_services: 0, unconverted_quotes: 0 };
  declinedSort : TableSortModel = { ...LIST_DEFAULT_SORTS.declined_work };
  declinedWork : Array<IntelligenceDeclinedWorkModel> = [];
  lapsedCustomers : Array<IntelligenceLapsedCustomerModel> = [];
  lapsedSort : TableSortModel = { ...LIST_DEFAULT_SORTS.lapsed_customers };
  listState : { [ list : string ] : { page : number; paged : Array<any>; paging : boolean } } = this.freshListState();
  overdueServices : Array<IntelligenceOverdueServiceModel> = [];
  overdueSort : TableSortModel = { ...LIST_DEFAULT_SORTS.overdue_services };
  pageSize : number = LIST_PAGE_SIZE;
  quotesSort : TableSortModel = { ...LIST_DEFAULT_SORTS.unconverted_quotes };
  unconvertedQuotes : Array<IntelligenceUnconvertedQuoteModel> = [];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    public intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService      : IntelligenceService,
    private messengerService        : MessengerService,
    private translateService        : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  contactEmail ( row : { customer_id : string; display_name : string; email : string } ) : void {
    this.messengerService.email({
      customer_id  : row.customer_id,
      email_address: row.email,
      name         : row.display_name
    }).catch(() => null);
  }

  contactSms ( row : { customer_id : string; display_name : string; mobile : string } ) : void {
    this.messengerService.sms({
      customer_id: row.customer_id,
      name       : row.display_name,
      send_to    : row.mobile
    }).catch(() => null);
  }

  countLabel ( list : string ) : string {
    return this.intelligenceFormatService.count(this.counts[ list ]);
  }

  date ( value : string ) : string {
    return this.intelligenceFormatService.date(value);
  }

  declinedCsvRows () : Array<any> {
    return this.declinedWork.map(row => ({
      customer   : row.display_name,
      make       : row.make,
      model      : row.model,
      item       : row.item,
      declined_on: row.declined_on,
      mobile     : row.mobile,
      email      : row.email,
      value      : row.value_ex_tax
    }));
  }

  declinedValue () : number {
    return this.declinedWork.reduce(( sum, row ) => sum + row.value_ex_tax, 0);
  }

  isDeclinedEmpty () : boolean {
    return !this.declinedWork.length;
  }

  isLapsedEmpty () : boolean {
    return !this.lapsedCustomers.length;
  }

  isOverdueEmpty () : boolean {
    return !this.overdueServices.length;
  }

  isQuotesEmpty () : boolean {
    return !this.unconvertedQuotes.length;
  }

  lapsedCsvRows () : Array<any> {
    return this.lapsedCustomers.map(row => ({
      customer      : row.display_name,
      last_visit    : row.last_visit_on,
      visits        : row.visits,
      lifetime_value: row.lifetime_value,
      mobile        : row.mobile,
      email         : row.email
    }));
  }

  // page one in default order comes with the tab payload; the rest comes from the paged endpoint
  listRows ( list : string ) : Array<any> {
    const state = this.listState[ list ];

    return state.page > 1 || !this.isDefaultListSort(list)
      ? state.paged
      : this.cacheFor(list);
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getOpportunities()
    .then(( data : IntelligenceOpportunitiesModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.declinedWork = data.declined_work || [];
      this.lapsedCustomers = data.lapsed_customers || [];
      this.overdueServices = data.overdue_services || [];
      this.unconvertedQuotes = data.unconverted_quotes || [];

      // true totals when the backend sends them, otherwise the visible rows with the pagers hidden
      this.counts = {
        declined_work     : data.declined_work_count || this.declinedWork.length,
        lapsed_customers  : data.lapsed_customers_count || this.lapsedCustomers.length,
        overdue_services  : data.overdue_services_count || this.overdueServices.length,
        unconverted_quotes: data.unconverted_quotes_count || this.unconvertedQuotes.length
      };

      this.declinedSort = { ...LIST_DEFAULT_SORTS.declined_work };
      this.lapsedSort = { ...LIST_DEFAULT_SORTS.lapsed_customers };
      this.listState = this.freshListState();
      this.overdueSort = { ...LIST_DEFAULT_SORTS.overdue_services };
      this.quotesSort = { ...LIST_DEFAULT_SORTS.unconverted_quotes };
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  monthsAgo ( value : string ) : string {
    if ( !value ) {
      return '';
    }

    const then = new Date(`${ value }T00:00:00`).getTime();

    if ( isNaN(then) ) {
      return '';
    }

    const months = Math.max(Math.round(( Date.now() - then ) / ( 30 * 24 * 3600 * 1000 )), 1);

    return this.translateService.instant('INTELLIGENCE_SPACE.OPPORTUNITIES.MONTHS_AGO', { months });
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  pageList ( list : string, page : number ) : void {
    const state = this.listState[ list ];

    if ( page < 1 || state.paging ) {
      return;
    }

    if ( page === 1 && this.isDefaultListSort(list) ) {
      state.page = 1;

      return;
    }

    const generation = this.loadGeneration;

    state.paging = true;

    this.intelligenceService.getOpportunitiesList(list, page, this.sortRef(list))
    .then(( result : IntelligenceListPageModel<any> ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      state.paged = result.rows || [];
      state.page = page;
    })
    .catch(() => null)
    .finally(() => state.paging = false);
  }

  overdueCsvRows () : Array<any> {
    return this.overdueServices.map(row => ({
      customer    : row.display_name,
      make        : row.make,
      model       : row.model,
      plate       : row.plate_number,
      service_due : row.service_due_on,
      days_overdue: row.days_overdue,
      mobile      : row.mobile,
      email       : row.email
    }));
  }

  quotesCsvRows () : Array<any> {
    return this.unconvertedQuotes.map(row => ({
      customer: row.display_name,
      quote   : row.quote_number,
      quoted  : row.quoted_on,
      age_days: row.age_days,
      mobile  : row.mobile,
      email   : row.email,
      value   : row.value_ex_tax
    }));
  }

  quotesValue () : number {
    return this.unconvertedQuotes.reduce(( sum, row ) => sum + row.value_ex_tax, 0);
  }

  // sorted server side across pages; page one refetches unless on the default order
  sortDeclinedBy ( field : string ) : void {
    this.declinedSort = this.toggledSort(this.declinedSort, field);

    this.pageList('declined_work', 1);
  }

  sortLapsedBy ( field : string ) : void {
    this.lapsedSort = this.toggledSort(this.lapsedSort, field);

    this.pageList('lapsed_customers', 1);
  }

  sortOverdueBy ( field : string ) : void {
    this.overdueSort = this.toggledSort(this.overdueSort, field);

    this.pageList('overdue_services', 1);
  }

  sortQuotesBy ( field : string ) : void {
    this.quotesSort = this.toggledSort(this.quotesSort, field);

    this.pageList('unconverted_quotes', 1);
  }

  private cacheFor ( list : string ) : Array<any> {
    switch ( list ) {
      case 'declined_work': return this.declinedWork;
      case 'lapsed_customers': return this.lapsedCustomers;
      case 'overdue_services': return this.overdueServices;
      default: return this.unconvertedQuotes;
    }
  }

  private freshListState () : { [ list : string ] : { page : number; paged : Array<any>; paging : boolean } } {
    return {
      declined_work     : { page: 1, paged: [], paging: false },
      lapsed_customers  : { page: 1, paged: [], paging: false },
      overdue_services  : { page: 1, paged: [], paging: false },
      unconverted_quotes: { page: 1, paged: [], paging: false }
    };
  }

  private isDefaultListSort ( list : string ) : boolean {
    const sort = this.sortRef(list);

    return sort.field === LIST_DEFAULT_SORTS[ list ].field
      && sort.direction === LIST_DEFAULT_SORTS[ list ].direction;
  }

  private sortRef ( list : string ) : TableSortModel {
    switch ( list ) {
      case 'declined_work': return this.declinedSort;
      case 'lapsed_customers': return this.lapsedSort;
      case 'overdue_services': return this.overdueSort;
      default: return this.quotesSort;
    }
  }

  private toggledSort ( sort : TableSortModel, field : string ) : TableSortModel {
    return {
      direction: sort.field === field && sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };
  }
}
